import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import { GameMode, ServerRegion } from '../domain/enums';

export type Player = {
  name: string;
  steamId: string;
};

export type Team = {
  id: string;
  name: string;
  playerSteamIds: string[];
};

export type ConfigData = {
  lobbyNameDefault: string;
  defaultLeagueId: number;
  defaultGameMode: number;
  defaultServerRegion: number;
  hasPassword: boolean;
  players: Player[];
  teams: Team[];
};

export const defaultConfig: ConfigData = {
  lobbyNameDefault: '',
  defaultLeagueId: 0,
  defaultGameMode: GameMode.CaptainsMode,
  defaultServerRegion: ServerRegion.EuropeWest,
  hasPassword: false,
  players: [],
  teams: [],
};

const CONFIG_KEY = 'appConfig';

/** Merges stored config over the defaults so a partial or older shape still loads. */
export function parseConfig(raw: string | null): ConfigData {
  if (!raw) return defaultConfig;
  try {
    const parsed = JSON.parse(raw) as Partial<ConfigData>;
    return {
      ...defaultConfig,
      ...parsed,
      players: Array.isArray(parsed.players) ? parsed.players : [],
      teams: Array.isArray(parsed.teams) ? parsed.teams : [],
    };
  } catch {
    return defaultConfig;
  }
}

type ConfigApi = {
  config: ConfigData;
  setConfig: (next: ConfigData) => Promise<boolean>;
  shared: boolean;
  saveError: string;
  updateConfig: (patch: Partial<ConfigData>) => void;
};

const ConfigContext = createContext<ConfigApi | null>(null);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfigState] = useState<ConfigData>(defaultConfig);
  const [shared, setShared] = useState(false);
  const [saveError, setSaveError] = useState('');
  const remoteRevision = useRef(0);
  const saving = useRef(false);
  const refreshShared = useCallback(async () => {
    if (saving.current) return;
    try {
      const remote = await api.get<{
        revision: number;
        catalog_ready: boolean;
        teams: Team[];
        players: Player[];
      }>('/api/v1/tournament');
      if (saving.current || !remote.revision || !remote.catalog_ready) return;
      try {
        if (!localStorage.getItem('browserRosterBackup'))
          localStorage.setItem(
            'browserRosterBackup',
            localStorage.getItem(CONFIG_KEY) ?? '{}',
          );
      } catch {
        /* Shared storage remains usable when local storage is unavailable. */
      }
      remoteRevision.current = remote.revision;
      setShared(true);
      setConfigState((current) =>
        JSON.stringify(current.teams) === JSON.stringify(remote.teams ?? []) &&
        JSON.stringify(current.players) === JSON.stringify(remote.players ?? [])
          ? current
          : {
              ...current,
              teams: remote.teams ?? [],
              players: remote.players ?? [],
            },
      );
    } catch {
      /* Keep existing configuration until shared storage connects. */
    }
  }, []);
  useEffect(() => {
    void refreshShared();
    const timer = setInterval(() => void refreshShared(), 5000);
    return () => clearInterval(timer);
  }, [refreshShared]);

  useEffect(() => {
    try {
      setConfigState(parseConfig(window.localStorage.getItem(CONFIG_KEY)));
    } catch {
      setConfigState(defaultConfig);
    }
  }, []);

  const setConfig = useCallback(
    async (next: ConfigData): Promise<boolean> => {
      if (saving.current) return false;
      setSaveError('');
      if (remoteRevision.current) {
        saving.current = true;
        try {
          await api.post('/api/v1/tournament/catalog', {
            revision: remoteRevision.current,
            teams: next.teams,
            players: next.players,
          });
        } catch (error) {
          setSaveError((error as Error).message);
          saving.current = false;
          await refreshShared();
          return false;
        }
        saving.current = false;
      }
      setConfigState(next);
      try {
        window.localStorage.setItem(CONFIG_KEY, JSON.stringify(next));
      } catch {
        // Private browsing or a full quota: keeping it in memory still works for
        // this session, so this is not worth interrupting the user over.
      }
      await refreshShared();
      return true;
    },
    [refreshShared],
  );

  const updateConfig = useCallback((patch: Partial<ConfigData>) => {
    setConfigState((current) => {
      const next = { ...current, ...patch };
      try {
        window.localStorage.setItem(CONFIG_KEY, JSON.stringify(next));
      } catch {
        /* see setConfig */
      }
      return next;
    });
  }, []);

  const value = useMemo<ConfigApi>(
    () => ({ config, setConfig, updateConfig, shared, saveError }),
    [config, setConfig, updateConfig, shared, saveError],
  );

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
}

export function useConfig(): ConfigApi {
  const context = useContext(ConfigContext);
  if (!context)
    throw new Error('useConfig must be used inside a ConfigProvider');
  return context;
}

/** Resolves a Steam ID to a configured player name, falling back to the ID. */
export function playerLabel(config: ConfigData, steamId: string): string {
  const player = config.players.find(
    (candidate) => candidate.steamId === steamId,
  );
  return player?.name?.trim() || steamId;
}
